import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Loader2, Sparkles, Users2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { checkUtilityOverlap, UtilityTemplateGroup } from '@/services/api/broadcast';
import { addPagesToGroup } from '@/services/api/utility-templates';
import type { UtilityConflictGroup } from '@/types/broadcast';
import type { MultiTemplateSelectionEntry } from '../types';
import {
    allInvolvedGroupIds,
    autoResolveConflicts,
    buildUtilityTemplateResolution,
    columnGroupIdForGroup,
    conflictGroupKey,
} from '../utils/multiTemplateResolution';

const DEBOUNCE_MS = 400;

interface UtilityOverlapResolverProps {
    selection: MultiTemplateSelectionEntry[];
    pageIds: string[];
    templatesById: Map<number, UtilityTemplateGroup>;
    conflictResolutions: Record<string, number>;
    onConflictResolutionsChange: (resolutions: Record<string, number>) => void;
    /** Reporta ao pai se a esteira está pronta para avançar (sem conflitos pendentes, sem loading). */
    onReadyChange: (ready: boolean) => void;
    /** Reporta o último resultado bruto de overlap-check, para cache em BroadcastFormData (revisão final). */
    onResultChange?: (result: {
        conflictGroups: UtilityConflictGroup[];
        exclusiveByGroup: Record<string, string[]>;
        coveredPageIds: string[];
        uncoveredPageIds: string[];
    } | null) => void;
}

export default function UtilityOverlapResolver({
    selection,
    pageIds,
    templatesById,
    conflictResolutions,
    onConflictResolutionsChange,
    onReadyChange,
    onResultChange,
}: UtilityOverlapResolverProps) {
    const [expandedUncovered, setExpandedUncovered] = useState(false);
    const [debouncedKey, setDebouncedKey] = useState('');

    // Colunas (principais) participam sempre; auxiliares também são utility
    // templates reais com sua própria aprovação por página, então entram no
    // mesmo pool de overlap — uma página pode conflitar entre o principal de
    // uma coluna e o auxiliar de outra, não só entre principais.
    const groupIds = useMemo(() => selection.map(s => s.groupId), [selection]);
    const allGroupIds = useMemo(() => allInvolvedGroupIds(selection), [selection]);

    // Debounce: só recalcula a chave de disparo depois de 400ms sem mudanças
    useEffect(() => {
        const key = JSON.stringify({ groupIds: [...allGroupIds].sort(), pageIds: [...pageIds].sort() });
        const handle = setTimeout(() => setDebouncedKey(key), DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [allGroupIds, pageIds]);

    const queryClient = useQueryClient();

    const overlapMutation = useMutation({
        mutationFn: checkUtilityOverlap,
    });

    // Uma mutation de "aprovar faltantes" por group_id — chaveada por
    // groupId para não misturar o loading de um template com o de outro.
    const [approvingGroupId, setApprovingGroupId] = useState<number | null>(null);
    const approveMutation = useMutation({
        mutationFn: ({ groupId, missingPageIds }: { groupId: number; missingPageIds: string[] }) =>
            addPagesToGroup(groupId, missingPageIds, true),
        onMutate: ({ groupId }) => setApprovingGroupId(groupId),
        onSettled: () => setApprovingGroupId(null),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['utility-groups'] });
            // Revalida cobertura — as páginas recém-aprovadas devem sumir de missing_by_group.
            overlapMutation.mutate({ group_ids: allGroupIds, page_ids: pageIds });
        },
    });

    useEffect(() => {
        if (groupIds.length < 1 || pageIds.length === 0 || !debouncedKey) return;
        overlapMutation.mutate({ group_ids: allGroupIds, page_ids: pageIds });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedKey]);

    const result = overlapMutation.data;
    // Descarta falso-conflito: um template e seu PRÓPRIO auxiliar aprovados
    // na mesma página não competem entre si — são a mesma coluna, enviados
    // em sequência pro mesmo lead. Só é conflito real quando os group_ids
    // envolvidos remapeiam para 2+ colunas DISTINTAS.
    const conflictGroups = useMemo(() => {
        const raw = result?.conflict_groups ?? [];
        return raw.filter(c => {
            const columns = new Set(
                c.conflicting_group_ids
                    .map(gid => columnGroupIdForGroup(gid, selection))
                    .filter((gid): gid is number => gid !== null)
            );
            return columns.size >= 2;
        });
    }, [result, selection]);
    const isLoading = overlapMutation.isPending;

    const resolutionSummary = useMemo(() => {
        if (!result) return null;
        return buildUtilityTemplateResolution({
            conflictGroups,
            exclusiveByGroup: result.exclusive_by_group,
            uncoveredPageIds: result.coverage.uncovered_page_ids,
            conflictResolutions,
            selection,
        });
    }, [result, conflictGroups, conflictResolutions, selection]);

    useEffect(() => {
        if (groupIds.length < 1) {
            onReadyChange(true);
            return;
        }
        if (isLoading || !result) {
            onReadyChange(false);
            return;
        }
        // Com conflictGroups já filtrado (exclui principal x seu próprio
        // auxiliar), hasPendingConflicts só é true havendo conflito real
        // entre colunas distintas — 1 coluna sozinha nunca bloqueia.
        onReadyChange(!resolutionSummary?.hasPendingConflicts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupIds.length, isLoading, result, resolutionSummary]);

    useEffect(() => {
        if (!result) {
            onResultChange?.(null);
            return;
        }
        onResultChange?.({
            conflictGroups,
            exclusiveByGroup: result.exclusive_by_group,
            coveredPageIds: result.coverage.covered_page_ids,
            uncoveredPageIds: result.coverage.uncovered_page_ids,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, conflictGroups]);

    if (groupIds.length < 1) {
        return null;
    }

    const templateName = (groupId: number) =>
        templatesById.get(groupId)?.template_name ?? `Template #${groupId}`;

    // Cada conflict.conflicting_group_ids pode conter auxiliares — remapeia
    // para o group_id da COLUNA (principal) dona, já que vencer sempre
    // significa a coluna inteira ganhar a página, nunca o auxiliar sozinho.
    const conflictingColumns = (conflict: UtilityConflictGroup): number[] => {
        const columns = new Set<number>();
        for (const rawGroupId of conflict.conflicting_group_ids) {
            const columnGroupId = columnGroupIdForGroup(rawGroupId, selection);
            if (columnGroupId !== null) columns.add(columnGroupId);
        }
        return Array.from(columns);
    };

    const handleAutoResolve = () => {
        const next = autoResolveConflicts(conflictGroups, selection, conflictResolutions);
        onConflictResolutionsChange(next);
    };

    const handlePickWinner = (conflict: UtilityConflictGroup, groupId: number) => {
        const key = conflictGroupKey(conflict);
        onConflictResolutionsChange({ ...conflictResolutions, [key]: groupId });
    };

    const pendingCount = conflictGroups.filter(c => conflictResolutions[conflictGroupKey(c)] === undefined).length;

    return (
        <div
            className={`space-y-4 rounded-xl border-2 bg-white p-4 transition-colors ${
                result && pendingCount > 0
                    ? 'border-red-200'
                    : result
                        ? 'border-emerald-200'
                        : 'border-gray-200'
            }`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Users2 className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-900">Cobertura e sobreposição</h4>
                </div>
                {isLoading && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Calculando...
                    </div>
                )}
            </div>

            {overlapMutation.isError && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Erro ao calcular cobertura/sobreposição. Tente ajustar a seleção.
                </div>
            )}

            {result && (
                <>
                    {/* Resumo de cobertura — cobertas é a métrica "boa" (verde,
                        maior peso visual); sem cobertura é informativo (âmbar,
                        secundário) já que não bloqueia salvar, só exclui do envio. */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                            <p className="text-xs text-emerald-600 font-medium">Cobertas</p>
                            <p className="text-2xl font-bold text-emerald-700 tabular-nums">{result.coverage.covered_page_ids.length}</p>
                        </div>
                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                            <p className="text-xs text-amber-600 font-medium">Sem cobertura</p>
                            <p className="text-2xl font-bold text-amber-700 tabular-nums">{result.coverage.uncovered_page_ids.length}</p>
                        </div>
                    </div>

                    {result.coverage.uncovered_page_ids.length > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                            <button
                                type="button"
                                onClick={() => setExpandedUncovered(v => !v)}
                                className="flex w-full items-center justify-between text-xs font-medium text-amber-800"
                            >
                                <span>
                                    {result.coverage.uncovered_page_ids.length} página(s) sem template aprovado — serão excluídas do broadcast
                                </span>
                                {expandedUncovered ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                            {expandedUncovered && (
                                <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                                    {result.coverage.uncovered_page_ids.map(id => (
                                        <p key={id} className="text-[11px] font-mono text-amber-700">{id}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Cobertura POR TEMPLATE — a união acima (uncovered_page_ids)
                        não diz quantas faltam em CADA template individualmente.
                        Usa allGroupIds (não groupIds): o auxiliar de cada coluna
                        também é um utility template real com aprovação própria
                        por página, então também precisa ser validado — sem isso
                        o auxiliar nunca aparecia como "faltando" mesmo quando
                        nenhuma página tinha ele aprovado. Botão dispara a
                        aprovação (add_pages_to_group) das páginas que faltam
                        especificamente naquele grupo (principal ou auxiliar). */}
                    {allGroupIds.length >= 1 && (
                        <div className="space-y-2">
                            {allGroupIds.map(groupId => {
                                const missing = result.missing_by_group?.[String(groupId)] ?? [];
                                if (missing.length === 0) return null;
                                const name = templateName(groupId);
                                const isSecondary = !selection.some(s => s.groupId === groupId);
                                const isApproving = approveMutation.isPending && approvingGroupId === groupId;
                                return (
                                    <div key={groupId} className="rounded-lg border border-amber-200 bg-white p-3 space-y-2">
                                        <p className="text-xs text-gray-700">
                                            <span className="font-semibold">{name}</span>
                                            {isSecondary && <span className="text-gray-400"> (auxiliar)</span>}
                                            : faltam{' '}
                                            <span className="font-bold text-amber-700 tabular-nums">{missing.length}</span>{' '}
                                            página(s) aprovada(s)
                                        </p>
                                        <Button
                                            type="button"
                                            size="sm"
                                            disabled={isApproving}
                                            onClick={() => approveMutation.mutate({ groupId, missingPageIds: missing })}
                                            className="h-7 text-xs gap-1.5 bg-[#0f172a] text-white hover:bg-[#1e293b]"
                                        >
                                            {isApproving ? (
                                                <>
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    Enviando para aprovação...
                                                </>
                                            ) : (
                                                <>Aprovar {missing.length} página(s) neste template</>
                                            )}
                                        </Button>
                                        {approveMutation.isSuccess && approveMutation.variables?.groupId === groupId && (
                                            <p className="text-[11px] text-emerald-700">
                                                Enviado — pode levar alguns minutos até a Meta processar.
                                            </p>
                                        )}
                                        {approveMutation.isError && approveMutation.variables?.groupId === groupId && (
                                            <p className="text-[11px] text-red-600">Falha ao enviar. Tente novamente.</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Grupos de conflito — o pendingCount é a métrica que mais
                        importa aqui (bloqueia o salvar), então ganha destaque
                        visual proporcional: badge vermelho pulsante quando > 0. */}
                    {conflictGroups.length > 0 ? (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <p className="text-xs font-medium text-gray-600">
                                        {conflictGroups.length} grupo(s) de conflito
                                    </p>
                                    {pendingCount > 0 ? (
                                        <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-600">
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                                            {pendingCount} pendente{pendingCount > 1 ? 's' : ''}
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-600">
                                            <CheckCircle2 className="h-3 w-3" />
                                            tudo resolvido
                                        </span>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={handleAutoResolve}
                                    disabled={pendingCount === 0}
                                    className="h-7 text-xs gap-1.5 border-[#7dd3fc] text-[#0f172a] hover:bg-[#7dd3fc]/10 disabled:opacity-40"
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    Auto-resolver
                                </Button>
                            </div>

                            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                {conflictGroups.map((conflict) => {
                                    const key = conflictGroupKey(conflict);
                                    const chosen = conflictResolutions[key];
                                    return (
                                        <div
                                            key={key}
                                            className={`rounded-lg border-2 p-3 transition-colors ${chosen !== undefined ? 'border-emerald-200 bg-emerald-50/40' : 'border-red-200 bg-red-50/40'}`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-xs text-gray-600">
                                                    <span className="text-base font-bold text-gray-900 tabular-nums">{conflict.page_ids.length}</span>
                                                    {' '}página(s) em conflito
                                                </p>
                                                {chosen !== undefined ? (
                                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                                ) : (
                                                    <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {conflictingColumns(conflict).map(columnGroupId => {
                                                    const secondaryGroupId = selection.find(
                                                        s => s.groupId === columnGroupId
                                                    )?.secondaryGroupId;
                                                    return (
                                                        <button
                                                            key={columnGroupId}
                                                            type="button"
                                                            onClick={() => handlePickWinner(conflict, columnGroupId)}
                                                            className={`px-2.5 py-1 rounded-full text-xs font-medium border-2 transition-all ${
                                                                chosen === columnGroupId
                                                                    ? 'bg-[#0f172a] text-white border-[#0f172a] scale-105'
                                                                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#7dd3fc] hover:scale-105'
                                                            }`}
                                                        >
                                                            {templateName(columnGroupId)}
                                                            {secondaryGroupId != null && (
                                                                <span className="opacity-70"> + {templateName(secondaryGroupId)}</span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                            <CheckCircle2 className="w-4 h-4 shrink-0" />
                            Nenhuma sobreposição entre os templates selecionados.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
